import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from './AdminLayout';
import { updateUser, login } from '../../services/apiService';

const AdminChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const userId = localStorage.getItem('userId');
    
    if (!isLoggedIn || !userId) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear the error for the edited field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from the current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Change password
  const handleChangePassword = async () => {
    // Validate form
    if (!validateForm()) {
      showNotification('Please fix the errors in the form', 'error');
      return;
    }

    try {
      setIsSaving(true);
      const userId = localStorage.getItem('userId');
      const userEmail = localStorage.getItem('userEmail');
      
      // Verify current password using the login API
      try {
        const loginResult = await login(userEmail, formData.currentPassword);
        if (!loginResult || !loginResult.success) {
          setErrors({ ...errors, currentPassword: 'Incorrect current password' });
          showNotification('Current password is incorrect', 'error');
          setIsSaving(false);
          return;
        }
      } catch (loginError) {
        setErrors({ ...errors, currentPassword: 'Incorrect current password' });
        showNotification('Current password is incorrect', 'error');
        setIsSaving(false);
        return;
      }
      
      // If verification succeeds, update the password
      const updatedUser = await updateUser(userId, {
        password: formData.newPassword
      });
      
      if (updatedUser) {
        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setErrors({});
        showNotification('Password changed successfully!', 'success');
      } else {
        showNotification('Error changing password', 'error');
      }
    } catch (error) {
      showNotification('Error changing password: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 25px rgba(150, 202, 45, 0.2)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: '800',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <i className="fas fa-key" />
            Change Password
          </h1>
          <p style={{
            fontSize: '16px',
            margin: '0',
            opacity: 0.9
          }}>
            Update your password to secure your account
          </p>
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '300px',
          animation: 'slideInRight 0.3s ease'
        }}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
          <span>{notification.message}</span>
        </div>
      )}

      {/* Password Change Form */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {/* Security Info */}
        <div style={{
          background: '#f8f9fa',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '32px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '8px'
          }}>
            <i className="fas fa-shield-alt" style={{ color: '#96ca2d', fontSize: '20px' }} />
            <h3 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: '600',
              color: '#212529'
            }}>
              Security tips
            </h3>
          </div>
          <ul style={{
            margin: '8px 0 0 0',
            paddingLeft: '24px',
            color: '#6c757d',
            fontSize: '14px',
            lineHeight: '1.8'
          }}>
            <li>Use at least 6 characters</li>
            <li>Mix letters, numbers, and symbols</li>
            <li>Avoid easy-to-guess passwords</li>
            <li>Never share your password</li>
          </ul>
        </div>

        {/* Form Fields */}
        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Current Password Field */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#212529',
              fontSize: '14px'
            }}>
              <i className="fas fa-lock" style={{ marginRight: '8px', color: '#96ca2d' }} />
              Current password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => handleChange('currentPassword', e.target.value)}
                placeholder="Enter your current password"
                style={{
                  width: '100%',
                  padding: '12px 48px 12px 16px',
                  border: `2px solid ${errors.currentPassword ? '#dc3545' : '#e9ecef'}`,
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: errors.currentPassword ? '#fff5f5' : '#ffffff'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#96ca2d';
                  e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.currentPassword ? '#dc3545' : '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className={`fas ${showPasswords.current ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
            {errors.currentPassword && (
              <div style={{
                color: '#dc3545',
                fontSize: '13px',
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-exclamation-circle" />
                {errors.currentPassword}
              </div>
            )}
          </div>

          {/* New Password Field */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#212529',
              fontSize: '14px'
            }}>
              <i className="fas fa-key" style={{ marginRight: '8px', color: '#96ca2d' }} />
              New password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleChange('newPassword', e.target.value)}
                placeholder="Enter your new password"
                style={{
                  width: '100%',
                  padding: '12px 48px 12px 16px',
                  border: `2px solid ${errors.newPassword ? '#dc3545' : '#e9ecef'}`,
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: errors.newPassword ? '#fff5f5' : '#ffffff'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#96ca2d';
                  e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.newPassword ? '#dc3545' : '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
            {errors.newPassword && (
              <div style={{
                color: '#dc3545',
                fontSize: '13px',
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-exclamation-circle" />
                {errors.newPassword}
              </div>
            )}
            {formData.newPassword && !errors.newPassword && (
              <div style={{
                color: '#10b981',
                fontSize: '13px',
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-check-circle" />
                Password looks good
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#212529',
              fontSize: '14px'
            }}>
              <i className="fas fa-lock" style={{ marginRight: '8px', color: '#96ca2d' }} />
              Confirm new password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                placeholder="Confirm your new password"
                style={{
                  width: '100%',
                  padding: '12px 48px 12px 16px',
                  border: `2px solid ${errors.confirmPassword ? '#dc3545' : '#e9ecef'}`,
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: errors.confirmPassword ? '#fff5f5' : '#ffffff'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#96ca2d';
                  e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.confirmPassword ? '#dc3545' : '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
            {errors.confirmPassword && (
              <div style={{
                color: '#dc3545',
                fontSize: '13px',
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-exclamation-circle" />
                {errors.confirmPassword}
              </div>
            )}
            {formData.confirmPassword && 
             formData.newPassword === formData.confirmPassword && 
             !errors.confirmPassword && (
              <div style={{
                color: '#10b981',
                fontSize: '13px',
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fas fa-check-circle" />
                Passwords match
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div style={{
          marginTop: '32px',
          paddingTop: '32px',
          borderTop: '1px solid #e9ecef'
        }}>
          <button
            onClick={handleChangePassword}
            disabled={isSaving}
            style={{
              width: '100%',
              padding: '14px 24px',
              borderRadius: '8px',
              border: 'none',
              background: isSaving ? '#9ca3af' : 'linear-gradient(135deg, #96ca2d, #7fb069)',
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: isSaving ? 'none' : '0 4px 15px rgba(150, 202, 45, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(150, 202, 45, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(150, 202, 45, 0.3)';
              }
            }}
          >
            {isSaving ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save" />
                Change password
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminChangePassword;

